import {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import dayjs from 'dayjs';

// import { useAuth } from './AuthContext';
// import { useQuery } from '@tanstack/react-query';
// import { axiosInstance } from '@/lib/axios';
// import { useUserDetails } from '@/hooks/useUserDetails';

import { useGetRegionWiseAccounts } from '@/lib/api/hooks/useAccounts';
import { useRegion } from '@/context/CountryContext';
interface Profile {
  accountId: string;
  id: number;
  profileId: string;
  linkedAccountId?: number;
  accountName: string;
  selected: boolean;
}

interface AdAccount {
  id: number;
  name: string;
  profiles: Profile[];
  selected: boolean;
}

interface HeaderStateType {
  // Page title of the current view
  pageTitle: string;
  setPageTitle: Dispatch<SetStateAction<string>>;

  // Ad accounts with multi-selection state
  adAccounts: AdAccount[];
  setAdAccounts: Dispatch<SetStateAction<AdAccount[]>>;

  // Dropdown visibility for Ad Accounts
  adAccountVisible: boolean;
  setAdAccountVisible: Dispatch<SetStateAction<boolean>>;

  // Date range using native Date objects
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
  setDateRange: Dispatch<SetStateAction<[dayjs.Dayjs, dayjs.Dayjs]>>;

  // Date picker popover visibility
  datePickerVisible: boolean;
  setDatePickerVisible: Dispatch<SetStateAction<boolean>>;

  // Current user role (not full user info)
  role: string;
  setRole: Dispatch<SetStateAction<string>>;
  // region wise filter
}

// Create the context
const HeaderState = createContext<HeaderStateType | undefined>(undefined);

export const HeaderStateProvider = ({ children }: { children: ReactNode }) => {
  // 1. Page title
  const getPageTitle = () => {
    return 'Dashboard'; // Default title
  };
  const [pageTitle, setPageTitle] = useState(getPageTitle());
  // const [selectedRegion, setSelectedRegion] = useState<SelectedRegion>({
  //   country: 'India',
  //   countrycode: 'IN',
  //   flag: '🇮🇳',
  // });

  const { selectedRegion } = useRegion();

  const { data: RegionWiseAccount, isFetched: RegionWiseAccountFetched } =
    useGetRegionWiseAccounts(selectedRegion.countrycode!);

  // const { user } = useAuth();
  // const { userDetails } = useUserDetails();

  // Determine which userId to use for fetching accounts
  // const effectiveUserId = userDetails?.createdById || userDetails?.id;

  // const { data: accountsData, isFetched } = useQuery({
  //   queryKey: ['accounts', effectiveUserId],
  //   queryFn: async () => {
  //     // If invited, fetch owner's accounts, else fetch current user's accounts
  //     if (userDetails?.createdById) {
  //       // const { data } = await axiosInstance.get(`/accounts?userId=${userDetails.createdById}`);
  //       // return data;
  //       const { data } = await axiosInstance.get('/accounts');
  //       return data;
  //     } else {
  //       const { data } = await axiosInstance.get('/accounts');
  //       return data;
  //     }
  //   },
  //   enabled: !!user && !!effectiveUserId,
  // });

  // const { data: accountsData, isFetched } = useQuery({
  //   queryKey: ['accounts', effectiveUserId],
  //   queryFn: async () => {
  //     // If invited, fetch owner's accounts, else fetch current user's accounts
  //     if (userDetails?.createdById) {
  //       const { data } = await axiosInstance.get(
  //         `/accounts?userId=${userDetails.createdById}`
  //       );
  //       return data;
  //     } else {
  //       const { data } = await axiosInstance.get('/accounts');
  //       return data;
  //     }
  //   },
  //   enabled: !!user && !!effectiveUserId,
  // });

  // 2. Ad accounts with multi-selection
  const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
  const [adAccountVisible, setAdAccountVisible] = useState(false);

  // 3. Date range
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ]);
  const [datePickerVisible, setDatePickerVisible] = useState(false);

  // 4. User role
  const [role, setRole] = useState('Manager');

  useEffect(() => {
    if (RegionWiseAccountFetched && RegionWiseAccount) {
      const accountsWithSelection = RegionWiseAccount.map((account: any) => ({
        ...account,
        selected: false,
        profiles: account.profiles.map((profile: any) => ({
          ...profile,
          selected: false,
        })),
      }));

      // Hydrate selection from persisted non-ads multi-select if present
      try {
        const raw = localStorage.getItem('app_v2_nonads_profiles');
        if (raw) {
          const stored = JSON.parse(raw);
          if (Array.isArray(stored) && stored.length > 0) {
            const hydrated = accountsWithSelection.map((account: any) => {
              const anySelected = account.profiles.some((p: any) => stored.includes(p.profileId));
              return {
                ...account,
                selected: anySelected,
                profiles: account.profiles.map((profile: any) => ({
                  ...profile,
                  selected: stored.includes(profile.profileId),
                })),
              };
            });
            setAdAccounts(hydrated);
            return;
          }
        }
      } catch {}

      setAdAccounts(accountsWithSelection);
    }
  }, [RegionWiseAccountFetched, RegionWiseAccount]);


  return (
    <HeaderState.Provider
      value={{
        pageTitle,
        setPageTitle,
        adAccounts,
        setAdAccounts,
        adAccountVisible,
        setAdAccountVisible,
        dateRange,
        setDateRange,
        datePickerVisible,
        setDatePickerVisible,
        role,
        setRole,
      }}
    >
      {children}
    </HeaderState.Provider>
  );
};

// Custom hook for consuming header state
export const useHeaderStateProvider = () => {
  const context = useContext(HeaderState);
  if (!context) {
    throw new Error(
      'useHeaderStateProvider must be used within HeaderStateProvider'
    );
  }
  return context;
};
